import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../utils/cn";
import { CountryFlag } from "./CountryFlag";
import {
  getEnabledCountries,
  type CountryConfig,
} from "@/shared/config/countries";
import { toast } from "sonner";

export interface Country {
  code: string;
  name: string;
  flag: string;
  enabled: boolean;
}

export interface CountryPickerProps {
  value: string;
  onChange: (countryCode: string) => void;
  label?: string;
  error?: string;
  className?: string;
  /** When true, countries display in a single row with horizontal scroll */
  scrollHorizontal?: boolean;
}

export const CountryPicker: React.FC<CountryPickerProps> = ({
  value,
  onChange,
  label,
  error,
  className,
  scrollHorizontal = false,
}) => {
  const { i18n } = useTranslation();
  const [countries, setCountries] = useState<CountryConfig[]>([]);
  const [loading, setLoading] = useState(true);

  // Load countries from database
  useEffect(() => {
    const loadCountries = async () => {
      try {
        setLoading(true);
        const enabledCountries = await getEnabledCountries();
        setCountries(enabledCountries);
      } catch (error) {
        console.error("Error loading countries:", error);
        toast.error("Error loading countries");
      } finally {
        setLoading(false);
      }
    };
    loadCountries();
  }, []);

  const gridClass = scrollHorizontal
    ? "flex flex-row flex-nowrap gap-3 overflow-x-auto pb-2 -mx-1 px-1"
    : "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3";

  if (loading) {
    return (
      <div className={cn("w-full", className)}>
        {label && (
          <label className="block text-sm font-medium text-tiber-600 mb-2">
            {label}
          </label>
        )}
        <div className={scrollHorizontal ? "flex gap-3 overflow-x-auto" : "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3"}>
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="flex flex-col items-center justify-center p-4 rounded-lg border-2 border-tiber-200 animate-pulse"
            >
              <div className="w-12 h-12 bg-gray-200 rounded-full mb-2" />
              <div className="w-16 h-4 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cn("w-full", className)}>
      {label && (
        <label className="block text-sm font-medium text-tiber-600 mb-2">
          {label}
        </label>
      )}
      <div className={cn(gridClass, scrollHorizontal && "min-w-0")}>
        {countries.map((country) => {
          const isEnabled = country.enabled;
          const countryName =
            i18n.language === "es" ? country.nameEs : country.nameEn;
          return (
            <button
              key={country.code}
              type="button"
              onClick={() => {
                if (isEnabled) {
                  onChange(country.code);
                }
              }}
              disabled={!isEnabled}
              className={cn(
                scrollHorizontal && "shrink-0",
                "flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all duration-200",
                "relative",
                isEnabled
                  ? "hover:border-tiber-600 hover:shadow-md cursor-pointer"
                  : "opacity-50 cursor-not-allowed",
                value === country.code && isEnabled
                  ? "border-tiber-600 bg-tiber-100 bg-opacity-10"
                  : isEnabled
                    ? "border-tiber-200"
                    : "border-tiber-200 bg-romance-50"
              )}
            >
              <CountryFlag
                countryCode={country.code}
                size="3em"
                className={cn("mb-2", !isEnabled && "opacity-50")}
                title={countryName}
              />
              <span
                className={cn(
                  "text-sm font-medium text-center",
                  !isEnabled && "text-titan"
                )}
              >
                {countryName}
              </span>
              {!isEnabled && (
                <span className="absolute top-1 right-1 text-xs bg-tiber-200 px-2 py-0.5 rounded text-tiber-700">
                  Soon
                </span>
              )}
            </button>
          );
        })}
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};

CountryPicker.displayName = "CountryPicker";
